/**
 * The laser devices: list, import and delete.
 *
 * A laser carries the parameter profile explorations mark with - its field
 * size, the grids its design values must sit on, and its calibration point.
 * Deletion is refused server-side while an exploration still references the
 * device, so the card's delete button asks and then reports that refusal
 * rather than hiding it.
 *
 * The page shares its look with the New exploration page: one card per
 * laser (components/ResourceCard.js) under a group label, with the summary
 * panel and the actions beside them (components/ResourcePanel.js).
 */
import React, { useEffect, useState } from "react";
import {
  Stack,
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Checkbox,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FlareRoundedIcon from "@mui/icons-material/FlareRounded";
import axios from "axios";
import ResourcePageLayout from "../../components/ResourcePageLayout";
import ResourcePanel, { PanelButton } from "../../components/ResourcePanel";
import ResourceCard, { DeleteResourceButton, usagePill } from "../../components/ResourceCard";
import FactTiles from "../../components/FactTiles";
import { LIST_SCOPE_ALL, withListScope } from "../../components/ListScopeToggle";
import { dataTableSx, overlineSx } from "../../components/cardChrome";
import { formatDateTime } from "../../helper/formatDate";

/**
 * Turn the laser_design_space_parameters object of a parameters_<laser>.json
 * into review rows. Throws with a readable message on malformed files.
 */
const parseParametersJson = (json) => {
  const params = json?.laser_design_space_parameters;
  if (
    !params ||
    typeof params !== "object" ||
    Array.isArray(params) ||
    Object.keys(params).length === 0
  ) {
    throw new Error(
      "The file has no 'laser_design_space_parameters' object — this does not look " +
        "like a laser parameters JSON (e.g. parameters_ipg.json)."
    );
  }
  return Object.entries(params).map(([key, spec]) => {
    const values = [...new Set(spec?.values || [])].sort((a, b) => a - b);
    if (values.length === 0 || !values.every((v) => typeof v === "number")) {
      throw new Error(`Parameter '${key}' has no valid numeric 'values' grid.`);
    }
    const limitMin = typeof spec.limit_min === "number" ? spec.limit_min : values[0];
    const limitMax =
      typeof spec.limit_max === "number" ? spec.limit_max : values[values.length - 1];
    return {
      key,
      values,
      limitMin,
      limitMax,
      exploreMin: String(values[0]),
      exploreMax: String(values[values.length - 1]),
      defaultValue: String(
        typeof spec.default_value === "number" ? spec.default_value : values[0]
      ),
      optimize: spec.optimize !== false,
    };
  });
};

const trimmedValues = (row) => {
  const min = parseFloat(row.exploreMin);
  const max = parseFloat(row.exploreMax);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
  return row.values.filter((v) => v >= min && v <= max);
};

const rowError = (row) => {
  const min = parseFloat(row.exploreMin);
  const max = parseFloat(row.exploreMax);
  const def = parseFloat(row.defaultValue);
  if (!Number.isFinite(min) || !Number.isFinite(max) || !Number.isFinite(def)) {
    return "not a number";
  }
  if (min > max) return "min > max";
  if (min < row.limitMin || max > row.limitMax) {
    return `outside hardware limits [${row.limitMin}, ${row.limitMax}]`;
  }
  const kept = trimmedValues(row).length;
  if (kept === 0) return "no grid points left";
  if (row.optimize && kept < 2) return "an explored parameter needs at least 2 grid points";
  if (def < min || def > max) return "default outside the explored range";
  return null;
};

/**
 * One laser: the header shows the parameters config and the usage; the
 * details (loaded on first expand from api/configs/) show the model facts
 * and the design-space grids.
 */
function LaserCard({ row, onDeleteRequest }) {
  const [detail, setDetail] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const usedBy = row.explorations_count ?? 0;

  const handleToggle = async (open) => {
    if (!open || !row.parameters_config_name || detail || detailBusy) return;
    setDetailBusy(true);
    setDetailError(null);
    try {
      const response = await axios.get(
        "api/configs/?name=" + encodeURIComponent(row.parameters_config_name)
      );
      setDetail(response.data?.[0]?.config_json || null);
    } catch (error) {
      console.error("Loading laser parameters failed:", error);
      setDetailError("Could not load the design-space parameters.");
    } finally {
      setDetailBusy(false);
    }
  };

  const parameters = detail?.laser_design_space_parameters || {};
  const pills = [
    row.parameters_config_name
      ? { key: "config", label: "Parameters", value: row.parameters_config_name, mono: true }
      : { key: "config", value: "Legacy, no config linked", tone: "missing" },
    usagePill(usedBy),
  ];

  return (
    <ResourceCard
      cardKey={row.id}
      icon={<FlareRoundedIcon sx={{ fontSize: 20 }} />}
      title={row.laser_device_name}
      caption={[row.created_by_email, row.created_at ? formatDateTime(row.created_at) : null]}
      pills={pills}
      actions={
        <DeleteResourceButton usedBy={usedBy} noun="laser" onClick={() => onDeleteRequest(row)} />
      }
      onToggle={handleToggle}
    >
      {!row.parameters_config_name ? (
        <Typography variant="body2" color="text.secondary">
          This laser predates the parameters upload and has no linked design-space config.
        </Typography>
      ) : detailBusy ? (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading design space…
          </Typography>
        </Stack>
      ) : detailError ? (
        <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
          {detailError}
        </Alert>
      ) : detail ? (
        <Stack spacing={2.5}>
          <FactTiles
            facts={[
              { label: "Model", value: detail.laser_model_name },
              { label: "Scanhead", value: detail.scanhead_model_name },
              {
                label: "Nominal power",
                value: detail.nominal_power != null ? `${detail.nominal_power} W` : null,
              },
              {
                label: "Marking area",
                value: Array.isArray(detail.marking_area_size)
                  ? `${detail.marking_area_size[0]}×${detail.marking_area_size[1]} mm`
                  : null,
              },
            ]}
          />
          <Box>
            <Typography variant="overline" sx={{ ...overlineSx, mb: 0.75 }}>
              Design space
            </Typography>
            <Table size="small" sx={{ maxWidth: 760, ...dataTableSx }}>
              <TableHead>
                <TableRow>
                  <TableCell>Parameter</TableCell>
                  <TableCell align="right">Explored range</TableCell>
                  <TableCell align="right">Grid points</TableCell>
                  <TableCell align="right">Default</TableCell>
                  <TableCell align="right">Hardware limits</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(parameters).map(([key, spec]) => {
                  const values = spec?.values || [];
                  return (
                    <TableRow key={key}>
                      <TableCell>{key}</TableCell>
                      <TableCell align="right">
                        {values.length ? `${values[0]} – ${values[values.length - 1]}` : "—"}
                      </TableCell>
                      <TableCell align="right">{values.length}</TableCell>
                      <TableCell align="right">{spec?.default_value ?? "—"}</TableCell>
                      <TableCell align="right">
                        {spec?.limit_min ?? "—"} – {spec?.limit_max ?? "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      ) : null}
    </ResourceCard>
  );
}

/**
 * Management page for laser systems. A laser is an immutable basis (like
 * substrates and camera calibrations): it is created ONCE from an uploaded
 * design-space parameters JSON (laserCommunication / algorithms
 * parameters_<laser>.json format), optionally trimmed in the review step, and
 * never edited afterwards — a different design space means a new laser.
 * Explorations clone the design space at creation time.
 */
export default function LasersOverview() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [importOpen, setImportOpen] = useState(false);
  const [importName, setImportName] = useState("");
  const [importRawJson, setImportRawJson] = useState(null);
  const [importFileName, setImportFileName] = useState("");
  const [importRows, setImportRows] = useState([]);
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const loadLasers = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await axios.get(
        withListScope("api/color/explorer/laser/device/", LIST_SCOPE_ALL)
      );
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Loading lasers failed:", error);
      setLoadError("Could not load the laser systems.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLasers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openImport = () => {
    setImportName("");
    setImportRawJson(null);
    setImportFileName("");
    setImportRows([]);
    setImportError(null);
    setImportOpen(true);
  };

  const handleParametersFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImportError(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result);
        setImportRows(parseParametersJson(json));
        setImportRawJson(json);
        setImportFileName(file.name);
      } catch (error) {
        console.error("Parsing parameters file failed:", error);
        setImportRawJson(null);
        setImportRows([]);
        setImportFileName("");
        setImportError(error.message || "Could not parse the parameters file.");
      }
    };
    reader.onerror = () => setImportError("Could not read the file.");
    reader.readAsText(file);
    // Allow re-selecting the same file after a fix.
    event.target.value = "";
  };

  const setImportRowField = (index, field, value) =>
    setImportRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );

  const importValid =
    importName.trim().length > 0 &&
    importRawJson !== null &&
    importRows.length > 0 &&
    importRows.every((row) => rowError(row) === null);

  const handleImportCreate = async () => {
    if (!importValid) return;
    setImportBusy(true);
    setImportError(null);
    try {
      // Rebuild the blob with the reviewed trims applied; everything else in
      // the uploaded file is passed through untouched.
      const configJson = JSON.parse(JSON.stringify(importRawJson));
      importRows.forEach((row) => {
        const spec = configJson.laser_design_space_parameters[row.key];
        const values = trimmedValues(row);
        const def = parseFloat(row.defaultValue);
        spec.values = values;
        spec.default_value = values.reduce(
          (best, v) => (Math.abs(v - def) < Math.abs(best - def) ? v : best),
          values[0]
        );
        spec.optimize = row.optimize;
      });
      const response = await axios.post("api/laser/device/import", {
        laserDeviceName: importName.trim(),
        configJson,
      });
      if (response.data?.Success) {
        setImportOpen(false);
        await loadLasers();
      } else {
        setImportError(response.data?.error || "Creating the laser failed.");
      }
    } catch (error) {
      console.error("Creating laser failed:", error);
      setImportError(
        error?.response?.data?.error || error?.message || "Creating the laser failed."
      );
    } finally {
      setImportBusy(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await axios.delete(
        `api/color/explorer/laser/device/${encodeURIComponent(deleteTarget.id)}/`
      );
      setDeleteTarget(null);
      await loadLasers();
    } catch (error) {
      console.error("Deleting laser failed:", error);
      setDeleteError(error?.response?.data?.error || "Could not delete the laser.");
    } finally {
      setDeleteBusy(false);
    }
  };

  const inUse = rows.filter((row) => (row.explorations_count ?? 0) > 0).length;
  const panel = (
    <ResourcePanel
      overline="All lasers"
      headline={loading ? "Loading" : `${rows.length} ${rows.length === 1 ? "laser" : "lasers"}`}
      subline={loading ? "" : `${inUse} in use · ${rows.length - inUse} unused`}
      about="A laser is created once from its design-space parameters file and never edited; different parameters mean a new laser. Explorations copy the design space when they are created, and a laser in use cannot be deleted."
      actions={
        <>
          <PanelButton primary startIcon={<AddCircleOutlineIcon />} onClick={openImport}>
            New laser
          </PanelButton>
          <PanelButton startIcon={<RefreshIcon />} onClick={loadLasers} disabled={loading}>
            Refresh
          </PanelButton>
        </>
      }
    />
  );

  return (
    <>
      <ResourcePageLayout
        title="Lasers"
        loading={loading}
        loadingLabel="Loading lasers…"
        error={loadError}
        count={rows.length}
        emptyText={"No lasers yet. Create one with “New laser”."}
        panel={panel}
      >
        {rows.map((row) => (
          <LaserCard
            key={row.id}
            row={row}
            onDeleteRequest={(target) => {
              setDeleteError(null);
              setDeleteTarget(target);
            }}
          />
        ))}
      </ResourcePageLayout>

      <Dialog
        open={importOpen}
        onClose={() => !importBusy && setImportOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>New laser</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              required
              label="Laser name"
              placeholder="e.g. JPT-M7-20W"
              value={importName}
              onChange={(event) => setImportName(event.target.value)}
              fullWidth
            />
            <Button
              component="label"
              variant={importRawJson ? "outlined" : "contained"}
              disableElevation
              startIcon={<UploadFileIcon />}
              sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 700, borderRadius: 2 }}
            >
              {importRawJson ? "Replace parameters file" : "Choose parameters file (.json)"}
              <input hidden accept=".json" type="file" onChange={handleParametersFile} />
            </Button>
            {importFileName ? (
              <Typography variant="body2" color="text.secondary">
                Loaded: <strong>{importFileName}</strong>
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Upload the laser's design-space parameters JSON
                (parameters_&lt;laser&gt;.json — the file that defines the power / speed /
                frequency / pulse-duration grids and hardware limits). The workstation
                profile is NOT needed here: its fixed marking parameters stay on the
                laser-communication side.
              </Typography>
            )}

            {importRows.length > 0 ? (
              <>
                <Typography variant="subtitle2" fontWeight={700}>
                  Review the design space
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Trim the explored ranges if needed (grid points outside are dropped);
                  hardware limits come from the file and cannot be widened. This is fixed at
                  creation — a different design space later means a new laser.
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "grey.50" } }}>
                        <TableCell>Parameter</TableCell>
                        <TableCell align="center">Explored</TableCell>
                        <TableCell>Min</TableCell>
                        <TableCell>Max</TableCell>
                        <TableCell>Default</TableCell>
                        <TableCell align="right">Grid points</TableCell>
                        <TableCell align="right">Hardware limits</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importRows.map((row, index) => {
                        const error = rowError(row);
                        return (
                          <TableRow key={row.key}>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {row.key}
                              {error ? (
                                <Typography variant="caption" color="error" display="block">
                                  {error}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                size="small"
                                checked={row.optimize}
                                onChange={(event) =>
                                  setImportRowField(index, "optimize", event.target.checked)
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                variant="standard"
                                value={row.exploreMin}
                                error={Boolean(error)}
                                onChange={(event) =>
                                  setImportRowField(index, "exploreMin", event.target.value)
                                }
                                sx={{ width: 90 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                variant="standard"
                                value={row.exploreMax}
                                error={Boolean(error)}
                                onChange={(event) =>
                                  setImportRowField(index, "exploreMax", event.target.value)
                                }
                                sx={{ width: 90 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                variant="standard"
                                value={row.defaultValue}
                                error={Boolean(error)}
                                onChange={(event) =>
                                  setImportRowField(index, "defaultValue", event.target.value)
                                }
                                sx={{ width: 90 }}
                              />
                            </TableCell>
                            <TableCell align="right">{trimmedValues(row).length}</TableCell>
                            <TableCell align="right">
                              {row.limitMin} – {row.limitMax}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : null}

            {importError ? (
              <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
                {importError}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)} disabled={importBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleImportCreate}
            disabled={!importValid || importBusy}
          >
            {importBusy ? "Creating…" : "Create laser"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleteBusy && setDeleteTarget(null)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete laser?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently removes the laser “{deleteTarget?.laser_device_name}” and its
            design-space parameters config. This cannot be undone.
          </DialogContentText>
          {deleteError ? (
            <Alert severity="error" variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
              {deleteError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleteBusy}
          >
            {deleteBusy ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
